export const NETWORK_EVERYONE_DEFAULT_PAGE_SIZE = 10;
export const NETWORK_EVERYONE_MAX_PAGE_SIZE = 50;
/** Admin agenda loads speakers with limit=100. Everyone stays at 50. */
export const NETWORK_TYPED_PARTICIPANT_MAX_PAGE_SIZE = 100;

/**
 * Person lists served by the Profile(eventId, userId) page query.
 * EXHIBITORS and SPONSORS are company documents and stay off this path.
 * Keep this aligned with ParticipantGroupType minus those two.
 */
export const INDEXED_PERSON_GROUP_TYPES = [
  "VISITORS",
  "SPEAKERS",
  "MEDIA",
  "SUPPORT",
  "OTHER",
] as const;

export type IndexedPersonGroupType =
  (typeof INDEXED_PERSON_GROUP_TYPES)[number];

export function isIndexedPersonGroupType(
  type?: string | null
): type is IndexedPersonGroupType {
  return (
    typeof type === "string" &&
    (INDEXED_PERSON_GROUP_TYPES as readonly string[]).includes(type)
  );
}

/**
 * Everyone and person-type lists, including text search and profile field
 * filters (`country` / `company` / `jobTitle`), use the indexed profile page.
 * Tags, suggestions, my-connections, a single participant group, and company
 * types stay on the legacy pipeline.
 *
 * `search` must be normalized first. Whitespace is not a text query.
 */
export function shouldUseIndexedParticipantPage(input: {
  type?: string | null;
  hasTags?: boolean;
  suggestion?: boolean;
  myConnections?: boolean;
  participantGroupId?: string | null;
}) {
  if (
    input.hasTags ||
    input.suggestion ||
    input.myConnections ||
    input.participantGroupId
  ) {
    return false;
  }
  if (!input.type) return true;
  return isIndexedPersonGroupType(input.type);
}

/** Stored equality values for indexed `country` / `company` / `jobTitle` filters. */
export type ProfileFieldEquals = {
  country?: string[];
  company?: string[];
  jobTitle?: string[];
};

export function hasProfileFieldEquals(equals?: ProfileFieldEquals | null) {
  return Boolean(
    equals?.country?.length ||
      equals?.company?.length ||
      equals?.jobTitle?.length
  );
}

/** Equality clauses for the typed Mongo `$match`. */
export function profileFieldEqualsClauses(equals?: ProfileFieldEquals | null) {
  if (!hasProfileFieldEquals(equals) || !equals) return {};
  return {
    ...(equals.country?.length ? { country: { $in: equals.country } } : {}),
    ...(equals.company?.length ? { company: { $in: equals.company } } : {}),
    ...(equals.jobTitle?.length ? { jobTitle: { $in: equals.jobTitle } } : {}),
  };
}

/** Equality clauses for Prisma Profile `where` (Everyone findMany/count). */
export function prismaProfileFieldEquals(equals?: ProfileFieldEquals | null) {
  if (!hasProfileFieldEquals(equals) || !equals) return {};
  return {
    ...(equals.country?.length ? { country: { in: equals.country } } : {}),
    ...(equals.company?.length ? { company: { in: equals.company } } : {}),
    ...(equals.jobTitle?.length ? { jobTitle: { in: equals.jobTitle } } : {}),
  };
}

/**
 * Trim, then drop regex metacharacters. The legacy listing used the stripped
 * string as a case-insensitive substring, so "a.b" searched for "ab".
 * Whitespace-only input, including `search=%20`, is not a search.
 */
export function normalizeParticipantSearchQuery(query?: string | null) {
  const trimmed = query?.trim() ?? "";
  if (!trimmed) return "";
  return trimmed.replace(/[.*+?^${}()|[\]\\]/g, "");
}

/** Case-insensitive substring. An empty needle matches nothing. */
export function participantTextMatches(
  query: string,
  values: Array<string | null | undefined>
) {
  const needle = query.trim().toLowerCase();
  if (!needle) return false;
  return values.some(
    (value) => typeof value === "string" && value.toLowerCase().includes(needle)
  );
}

export function compareDirectoryUserId(left: string, right: string) {
  const a = left.toLowerCase();
  const b = right.toLowerCase();
  if (a < b) return -1;
  if (a > b) return 1;
  return 0;
}

export function isAfterUserCursor(userId: string, cursor: string) {
  return compareDirectoryUserId(userId, cursor) > 0;
}

export type EveryoneConnectionStatus = "ACCEPTED" | "PENDING" | "REJECTED";

export function normalizeEveryonePagination(
  page?: number,
  take?: number,
  maxPageSize: number = NETWORK_EVERYONE_MAX_PAGE_SIZE
) {
  const normalizedPage =
    Number.isFinite(page) && (page ?? 0) > 0 ? Math.floor(page as number) : 1;
  const requestedTake =
    Number.isFinite(take) && (take ?? 0) > 0
      ? Math.floor(take as number)
      : NETWORK_EVERYONE_DEFAULT_PAGE_SIZE;
  const cap =
    Number.isFinite(maxPageSize) && maxPageSize > 0
      ? Math.floor(maxPageSize)
      : NETWORK_EVERYONE_MAX_PAGE_SIZE;

  return {
    page: normalizedPage,
    take: Math.min(requestedTake, cap),
  };
}

export type TypedProfileKey = {
  id: string;
  userId: string;
  createdAt?: Date | null;
};

/** Plain match so Mongo can bound the scan to the group index. No `$expr`. */
export function typedProfileMatch(input: {
  eventId: string;
  groupIds: string[];
  afterUserId?: string;
  onlyUserId?: string;
  fieldEquals?: ProfileFieldEquals | null;
}) {
  const userId = input.onlyUserId
    ? { $oid: input.onlyUserId }
    : input.afterUserId
      ? { $gt: { $oid: input.afterUserId } }
      : { $ne: null };

  return {
    eventId: { $oid: input.eventId },
    active: true,
    userId,
    participantGroupIds: {
      $in: input.groupIds.map((id) => ({ $oid: id })),
    },
    ...profileFieldEqualsClauses(input.fieldEquals),
  };
}

export function typedProfileKeyPipeline(input: {
  eventId: string;
  groupIds: string[];
  limit: number;
  afterUserId?: string;
  onlyUserId?: string;
  fieldEquals?: ProfileFieldEquals | null;
}) {
  const pipeline: object[] = [{ $match: typedProfileMatch(input) }];
  if (!input.onlyUserId) pipeline.push({ $sort: { userId: 1 } });
  pipeline.push(
    { $limit: input.limit },
    { $project: { _id: 1, userId: 1, createdAt: 1 } }
  );
  return pipeline;
}

export function typedProfileCountPipeline(input: {
  eventId: string;
  groupIds: string[];
  fieldEquals?: ProfileFieldEquals | null;
}) {
  return [{ $match: typedProfileMatch(input) }, { $count: "total" }];
}

/**
 * Every profile in the requested groups, via the same plain match as a typed
 * page. No `$regex`, `$expr`, `$sort`, or `$facet`: a regex in the match makes
 * Mongo drop the group index, and sorting the whole group is a blocking sort.
 * Callers filter name/email and order by userId in process.
 * Optional profile field equals stay equality `$in` so indexes stay usable.
 */
export function participantSearchKeyPipeline(input: {
  eventId: string;
  groupIds: string[];
  fieldEquals?: ProfileFieldEquals | null;
}) {
  return [
    { $match: typedProfileMatch(input) },
    { $project: { _id: 1, userId: 1, createdAt: 1, name: 1 } },
  ];
}

export type SearchProfileRow = {
  id: string;
  userId: string;
  createdAt?: Date | null;
  name?: string | null;
};

export type SearchUserHit = {
  id: string;
  userId: string;
  createdAt: Date | null;
  /** Profile name missed. User name and email still might match. */
  needsUserLookup: boolean;
};

/**
 * One row per user. The newest profile is the card, matching Everyone and
 * the legacy `$first` after `createdAt` desc. A name hit on any of that
 * user's profiles still counts as a match so an older title is not lost.
 * `rows` may arrive in any order; callers sort the hits by userId.
 */
export function collapseSearchProfiles(
  rows: SearchProfileRow[],
  query: string
): SearchUserHit[] {
  const order: string[] = [];
  const byUser = new Map<
    string,
    {
      newestId: string;
      newestAt: number;
      newestCreatedAt: Date | null;
      nameMatched: boolean;
    }
  >();

  for (const row of rows) {
    if (!row.userId || !row.id) continue;
    const at = row.createdAt?.getTime() ?? 0;
    const nameMatches = participantTextMatches(query, [row.name]);
    const acc = byUser.get(row.userId);
    if (!acc) {
      byUser.set(row.userId, {
        newestId: row.id,
        newestAt: at,
        newestCreatedAt: row.createdAt ?? null,
        nameMatched: nameMatches,
      });
      order.push(row.userId);
      continue;
    }
    if (at > acc.newestAt) {
      acc.newestId = row.id;
      acc.newestAt = at;
      acc.newestCreatedAt = row.createdAt ?? null;
    }
    if (nameMatches) acc.nameMatched = true;
  }

  return order.flatMap((userId) => {
    const acc = byUser.get(userId);
    if (!acc) return [];
    return [
      {
        id: acc.newestId,
        userId,
        createdAt: acc.newestCreatedAt,
        needsUserLookup: !acc.nameMatched,
      },
    ];
  });
}

export type ParticipantSearchUser = {
  name?: string | null;
  email?: string | null;
};

/**
 * Legacy directory search matched user name, profile name, and user email.
 * Profile-name hits are already decided. The rest match on the user document.
 */
export function resolveSearchHits(
  hits: SearchUserHit[],
  users: ReadonlyMap<string, ParticipantSearchUser>,
  query: string
): SearchUserHit[] {
  const matched: SearchUserHit[] = [];
  for (const hit of hits) {
    if (!hit.needsUserLookup) {
      matched.push(hit);
      continue;
    }
    const user = users.get(hit.userId);
    if (participantTextMatches(query, [user?.name, user?.email])) {
      matched.push(hit);
    }
  }
  return matched;
}

export function orderSearchHits<T extends { userId: string }>(hits: T[]): T[] {
  return [...hits].sort((left, right) =>
    compareDirectoryUserId(left.userId, right.userId)
  );
}

/**
 * Keep the newest profile per user while preserving userId order.
 * `batch` must already be ordered by userId.
 */
export function mergeTypedProfileKeys<T extends TypedProfileKey>(
  existing: T[],
  batch: T[]
): T[] {
  const byUser = new Map(existing.map((row) => [row.userId, row]));
  const order = existing.map((row) => row.userId);
  for (const row of batch) {
    if (!row.userId) continue;
    const kept = byUser.get(row.userId);
    if (!kept) {
      byUser.set(row.userId, row);
      order.push(row.userId);
      continue;
    }
    if ((row.createdAt?.getTime() ?? 0) > (kept.createdAt?.getTime() ?? 0)) {
      byUser.set(row.userId, row);
    }
  }
  return order.map((userId) => byUser.get(userId) as T);
}

export function sliceTypedUserPage<T extends { userId: string }>(
  users: T[],
  skipUsers: number,
  take: number
) {
  const page = users.slice(skipUsers, skipUsers + take);
  return {
    page,
    hasNextPage: users.length > skipUsers + take,
    nextCursor: users.length > skipUsers + take
      ? page[page.length - 1]?.userId ?? null
      : null,
  };
}

export function isObjectIdCursor(cursor?: string): cursor is string {
  return typeof cursor === "string" && /^[a-f\d]{24}$/i.test(cursor);
}

export type EveryoneRow = { userId: string | null; createdAt?: Date | null };

/**
 * The legacy pipeline grouped rows by user before paginating, so a user with
 * more than one profile in the event produced a single card, and its profile
 * lookup sorted `createdAt` descending so the newest profile won.
 */
export function dedupeByUserId<T extends EveryoneRow>(rows: T[]): T[] {
  const indexByUser = new Map<string, number>();
  const deduped: T[] = [];
  for (const row of rows) {
    if (!row.userId) continue;
    const existing = indexByUser.get(row.userId);
    if (existing === undefined) {
      indexByUser.set(row.userId, deduped.length);
      deduped.push(row);
      continue;
    }
    const kept = deduped[existing];
    if ((row.createdAt?.getTime() ?? 0) > (kept.createdAt?.getTime() ?? 0)) {
      deduped[existing] = row;
    }
  }
  return deduped;
}

/**
 * The legacy lookup surfaced whichever request document Mongo returned first,
 * including rejected ones. Keep rejections visible but resolve ties in favour
 * of the strongest relationship instead of document order.
 */
export function pickDirectoryConnectionStatus(
  statuses: Array<EveryoneConnectionStatus | null | undefined>
): EveryoneConnectionStatus | null {
  let pending: EveryoneConnectionStatus | null = null;
  let rejected: EveryoneConnectionStatus | null = null;
  for (const status of statuses) {
    if (status === "ACCEPTED") return "ACCEPTED";
    if (status === "PENDING") pending = status;
    if (status === "REJECTED" && !rejected) rejected = status;
  }
  return pending ?? rejected;
}

export function emptyEveryonePage(page: number) {
  return {
    data: [] as unknown[],
    pagination: {
      total: 0,
      hasNextPage: false,
      hasPreviousPage: page > 1,
      currentPage: page,
      nextCursor: null as string | null,
    },
  };
}

export function everyonePageResult<T extends EveryoneRow>(
  rows: T[],
  take: number,
  page: number,
  total: number,
  cursor?: string
) {
  const hasNextPage = rows.length > take;
  const window = hasNextPage ? rows.slice(0, take) : rows;
  const data = dedupeByUserId(window);

  return {
    data,
    pagination: {
      total,
      hasNextPage,
      hasPreviousPage: Boolean(cursor) || page > 1,
      currentPage: page,
      nextCursor: hasNextPage
        ? window[window.length - 1]?.userId ?? null
        : null,
    },
  };
}
